<template>
  <div v-if="type === 'phone'" class="phone-input-wrapper">
    <input
      ref="inputRef"
      :value="displayValue"
      :placeholder="placeholder"
      @input="handlePhoneInput"
      @focus="handlePhoneFocus"
      @blur="handlePhoneBlur"
      @keydown="handlePhoneKeyDown"
      class="base-input color-text-primary phone-input caption-medium"
      :class="{ error: hasError }"
      type="tel"
      :maxlength="maxLength + 6"
    />
  </div>
  <div v-else-if="type === 'digit'" class="text-input-wrapper">
    <input
      ref="inputRef"
      type="text"
      v-model="model"
      :placeholder="placeholder"
      @input="handleDigitInput"
      :maxlength="maxLength"
      class="base-input color-text-primary text-input caption-medium"
      :class="{ error: hasError }"
    />
  </div>
  <div v-else-if="type === 'email'">
    <input
      ref="inputRef"
      type="email"
      v-model="model"
      :placeholder="placeholder"
      @input="handleEmailInput"
      @blur="validateEmail"
      @focus="handleEmailFocus"
      class="base-input color-text-primary text-input caption-medium"
      :class="{ error: hasError }"
    />
  </div>
  <div v-else-if="type === 'text'">
    <input
      ref="inputRef"
      type="text"
      v-model="model"
      :placeholder="placeholder"
      class="base-input color-text-primary text-input caption-medium"
      :class="{ error: hasError }"
    />
  </div>
  <div v-else-if="type === 'code'">
    <div class="code-input">
      <input
        v-for="(_, index) in Array(codeLength).fill(0)"
        :key="index"
        :ref="
          (el: HTMLInputElement) => {
            inputRefs[index] = el;
          }
        "
        v-model="codeValues[index]"
        @input="(e: Event) => handleCodeInput(e, index)"
        @keydown="(e: Event) => handleCodeKeyDown(e, index)"
        @paste="handleCodePaste"
        @focus="handleCodeFocus"
        maxlength="1"
        class="code-input__box h2 color-text-primary"
        type="tel"
      />
    </div>
  </div>
  <div v-else-if="type === 'area'">
    <textarea
      class="area-input base-input color-text-primary caption-medium"
      v-model="model"
      name="text-area"
      id="text-area"
      cols="30"
      rows="10"
      :placeholder="placeholder"
    ></textarea>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  nextTick,
  defineProps,
  defineEmits,
  defineModel,
  withDefaults,
  onMounted,
} from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: string;
    type: "phone" | "code" | "digit" | "email" | "text" | "area";
    placeholder?: string;
    maxLength?: number;
    codeLength?: number;
    hasError?: boolean;
  }>(),
  {
    placeholder: "+7 (___) ___-__-__",
    maxLength: 10,
    codeLength: 4,
    hasError: false,
  }
);

const model = defineModel();

const emit = defineEmits([
  "update:modelValue",
  "complete",
  "validation",
  "change",
  "focus",
]);

const inputRef = ref<HTMLInputElement | null>(null);
const inputRefs = ref<HTMLInputElement[]>([]);

const displayValue = ref(props.modelValue);
const codeValues = ref<string[]>(Array(props.codeLength).fill(""));

const allowedKeys = [
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
];

const validatePhone = () => {
  const digits = displayValue.value.replace(/\D/g, "");
  const isValid = digits.length === 11;
  emit("validation", { isValidPhone: isValid });
  return isValid;
};

watch(
  () => props.modelValue,
  (newVal) => {
    emit("change", newVal);
    if (props.type === "phone") {
      displayValue.value = newVal;
      validatePhone();
    } else if (props.type === "code") {
      const newValues = newVal.split("").slice(0, props.codeLength);
      for (let i = 0; i < props.codeLength; i++) {
        codeValues.value[i] = newValues[i] || "";
      }
    }
  }
);

const handlePhoneFocus = () => {
  if (displayValue.value === "") {
    displayValue.value = "+7 (";
    nextTick(() => {
      if (inputRef.value) {
        inputRef.value.setSelectionRange(4, 4);
      }
    });
    updatePhoneModel();
  }
};

const handlePhoneBlur = () => {
  if (displayValue.value === "+7 (") {
    displayValue.value = "";
    updatePhoneModel();
  }
};

const handlePhoneKeyDown = (e: KeyboardEvent) => {
  if (!inputRef.value) return;

  const start = inputRef.value.selectionStart ?? 0;
  const end = inputRef.value.selectionEnd ?? 0;

  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
    return;
  }

  if (!/\d/.test(e.key)) {
    e.preventDefault();
    return;
  }

  if (e.key === "Backspace") {
    if (start === end) {
      if (start <= 4) e.preventDefault();
    } else {
      if (start < 4 || end <= 4) e.preventDefault();
    }
  } else if (e.key === "Delete") {
    if (start === end) {
      if (start < 4) e.preventDefault();
    } else {
      if (start < 4) e.preventDefault();
    }
  }
};

const handleDigitInput = (e: Event) => {
  console.log("digit0", e.target?.value);
  if (!e.target?.value) return;
  let value = e.target?.value;
  value = value.replace(/[^0-9]/g, "");
  e.target.value = value;
  model.value = value;
  console.log("digit1", model.value);
};

const handleEmailInput = (e: Event) => {
  let value = e.target?.value;
  value = value.replace(/[^a-zA-Z0-9@+._%-]/g, "");
  model.value = value;
};

const handleEmailFocus = (): void => {
  emit("focus");
};

const validateEmail = (): void => {
  const emailRegex =
    /^[a-zA-Z0-9+._%-]{1,256}@[a-zA-Z0-9][a-zA-Z0-9-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,25})+$/;
  if (model.value) {
    emit("validation", emailRegex.test(model.value as string));
  } else emit("validation", true);
};

const handlePhoneInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let value = target.value;

  let digits = value.replace(/\D/g, "");

  let validPhone = digits;
  if (
    digits.length === 12 &&
    (digits.startsWith("77") || digits.startsWith("78"))
  ) {
    validPhone = digits.substring(1, 12);
  }

  const maxDigits = props.maxLength + 1;
  const limitedDigits = validPhone.substring(0, maxDigits);

  let formatted = "+7 (";
  if (limitedDigits.length > 1) {
    const rest = limitedDigits.substring(1);
    formatted += rest.substring(0, 3);
    if (rest.length > 3) {
      formatted += ") " + rest.substring(3, 6);
    }
    if (rest.length > 6) {
      formatted += "-" + rest.substring(6, 8);
    }
    if (rest.length > 8) {
      formatted += "-" + rest.substring(8, 10);
    }
  }

  displayValue.value = formatted;
  updatePhoneModel();
  validatePhone();

  nextTick(() => {
    if (inputRef.value) {
      const pos = formatted.length;
      inputRef.value.setSelectionRange(pos, pos);
    }
  });
};

const updatePhoneModel = () => {
  emit("update:modelValue", displayValue.value);
};

const handleCodeInput = (e: Event, index: number) => {
  const target = e.target as HTMLInputElement;
  const value = target.value;

  if (value.length > 1) {
    const lastChar = value.charAt(value.length - 1);
    codeValues.value[index] = lastChar;
    focusNextCodeInput(index);
    updateCodeModel();
    return;
  }

  if (value) {
    focusNextCodeInput(index);
  }

  updateCodeModel();
};

const handleCodeKeyDown = (e: KeyboardEvent, index: number) => {
  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
    if (e.key === "Backspace" && !codeValues.value[index] && index > 0) {
      e.preventDefault();
      codeValues.value[index - 1] = "";
      focusPrevCodeInput(index);
      updateCodeModel();
    }
    return;
  }

  if (!/\d/.test(e.key)) e.preventDefault();
};

const handleCodePaste = (e: ClipboardEvent) => {
  e.preventDefault();
  const pasteData =
    e.clipboardData?.getData("text/plain").replace(/\D/g, "") || "";

  const limitedPaste = pasteData.substring(0, props.codeLength);

  for (let i = 0; i < props.codeLength; i++) {
    codeValues.value[i] = i < limitedPaste.length ? limitedPaste[i] : "";
  }

  updateCodeModel();

  const lastFilledIndex = Math.min(
    limitedPaste.length - 1,
    props.codeLength - 1
  );
  focusCodeInput(lastFilledIndex);
};

const handleCodeFocus = () => {
  const firstEmptyIndex = codeValues.value.findIndex((val) => val === "");
  if (firstEmptyIndex !== -1) {
    focusCodeInput(firstEmptyIndex);
  } else {
    focusCodeInput(props.codeLength - 1);
  }
};

const focusCodeInput = (index: number) => {
  nextTick(() => {
    const input = inputRefs.value[index];
    if (input) {
      input.focus();
    }
  });
};

const focusNextCodeInput = (index: number) => {
  if (index < props.codeLength - 1) {
    focusCodeInput(index + 1);
  } else {
    emit("complete");
  }
};

const focusPrevCodeInput = (index: number) => {
  if (index > 0) {
    focusCodeInput(index - 1);
  }
};

const updateCodeModel = () => {
  const code = codeValues.value.join("");
  emit("update:modelValue", code);
  if (code.length === props.codeLength) {
    emit("complete");
  }
};
const validationValueInit = () => {
  if (props.type === "email" && model.value) {
    validateEmail();
  }
};
onMounted(() => {
  validationValueInit();
});
</script>

<style scoped lang="scss">
@import "@/assets/style/main";

.phone-input {
  background: var(--color-bg-primary);
  //border: none;
  border: 1px solid transparent;
  width: 100%;
  padding: 24px 0 20px 20px;
  font-family: inherit;
  border-radius: 12px;

  &::placeholder {
    color: var(--color-grey-light);
    opacity: 1;
  }

  &:focus {
    //border: none;
    outline: none;
  }

  &:active {
    // border: none;
  }
}

.text-input {
  background: var(--color-bg-primary);
  width: 100%;
  padding: 24px 0 20px 20px;
  font-family: inherit;
  border-radius: 12px;
  border: 1px solid transparent;

  @media (max-width: 768px) {
    background: var(--color-bg-input);
  }

  &::placeholder {
    color: var(--color-grey-light);
    opacity: 1;
  }

  &:focus {
    outline: none;
  }
}

.error {
  color: var(--color-danger-primary);
  border-color: var(--color-danger-primary);
}

.code-input {
  display: flex;
  gap: 16px;

  &__box {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    border: none;
    background: var(--color-bg-primary);
    text-align: center;
    font-size: 24px;

    &:focus {
      border: none;
      outline: none;
    }

    &:active {
      border: none;
    }
  }
}

.area-input {
  width: 100%;
  border: none;
  height: 120px;
  box-sizing: border-box;
  padding: 24px 20px 20px 20px;
  border-radius: 12px;
  background: var(--color-bg-primary);
  outline: none;
  resize: none;

  &::placeholder {
    color: var(--color-grey-light);
    opacity: 1;
  }
}
</style>
